<template>
  <div class="camps-page">
    <div class="camp-banner" aria-label="營隊主視覺">
      <b-carousel
        v-if="bannerList.length > 0"
        :interval="5000"
        controls
        indicators
        background="#f0f9ff"
      >
        <b-carousel-slide v-for="banner in bannerList" :key="banner.Id">
          <template #img>
            <div
              class="camp-banner-frame"
              :class="{ 'has-link': !!banner.Link }"
              role="img"
              :aria-label="banner.Title || '營隊 Banner'"
              @click="openBannerLink(banner)"
            >
              <span
                class="camp-banner-backdrop"
                :style="{ backgroundImage: `url('${bannerUrl(banner)}')` }"
                aria-hidden="true"
              ></span>
              <img
                class="camp-banner-image"
                :src="bannerUrl(banner)"
                :alt="banner.Title || '營隊 Banner'"
              />
            </div>
          </template>
        </b-carousel-slide>
      </b-carousel>
      <div v-else class="camp-banner-empty">
        <b-container>探索逢甲大學營隊課程</b-container>
      </div>
    </div>

    <b-container class="py-5">
      <section class="camp-discovery-panel" aria-label="營隊搜尋與篩選">
        <div class="camp-discovery-heading">
          <div>
            <span class="section-kicker">FIND YOUR CAMP</span>
            <h2>找到適合你的營隊</h2>
            <p class="section-description">
              依照興趣、課程類型與招生狀態，快速找到適合的學習體驗
            </p>
          </div>

          <div class="series-header-actions">
            <b-button
              class="overview-btn"
              variant="outline-primary"
              @click="_go('./campOverview')"
            >
              <b-icon icon="grid-3x3-gap" class="mr-1" aria-hidden="true"></b-icon>
              課程總覽
            </b-button>
            <b-button
              v-if="canShowRefundPolicy"
              class="refund-policy-btn"
              variant="outline-secondary"
              @click="$bvModal.show('modal-camp-refund-policy')"
            >
              <b-icon icon="file-earmark-text" aria-hidden="true"></b-icon>
              退費規則
            </b-button>
          </div>
        </div>

        <div class="series-toolbar">
          <div class="camp-filter-block">
            <span class="camp-filter-caption">課程類型</span>
            <div class="camp-type-filter" v-if="typeOptions.length > 0">
              <button
                v-for="type in typeOptions"
                :key="type.value"
                type="button"
                class="type-btn"
                :class="{ active: Param.Type === type.value }"
                @click="selectType(type.value)"
              >
                {{ type.text }}
              </button>
            </div>
          </div>

          <div class="camp-filter-block camp-status-block">
            <span class="camp-filter-caption">報名狀態</span>
            <div class="camp-type-filter" role="group" aria-label="報名狀態篩選">
              <button
                v-for="status in applyStatusOptions"
                :key="status.value"
                type="button"
                class="type-btn"
                :class="{ active: Param.ApplyStatus === status.value }"
                :aria-pressed="Param.ApplyStatus === status.value"
                @click="selectApplyStatus(status.value)"
              >
                {{ status.text }}
              </button>
            </div>
          </div>

          <div class="sort-box">
            <span class="camp-filter-caption">排序方式</span>
            <b-form-select
              v-model="Param.SortMode"
              :options="sortOptions"
              @change="onSortChange"
            ></b-form-select>
          </div>
        </div>
      </section>

      <template v-if="loading">
        <div class="loading-block">
          <b-spinner variant="primary"></b-spinner>
        </div>
      </template>

      <b-alert
        v-else-if="seriesList.length === 0"
        show
        variant="warning"
        class="text-center"
      >
        目前尚無可瀏覽的營隊系列。
      </b-alert>

      <template v-else>
        <!-- 熱門系列 -->
        <section
          v-if="featuredSeriesList.length > 0"
          class="featured-series-section"
        >
          <div class="featured-section-heading">
            <div>
              <span class="section-kicker">FEATURED SERIES</span>
              <h2>熱門系列 <span class="section-count">{{ featuredSeriesList.length }}</span></h2>
              <p class="featured-description">
                精選人氣營隊，開啟精彩學習旅程
              </p>
            </div>
          </div>

          <div
            class="featured-series-carousel"
            :class="[
              `featured-count-${Math.min(featuredSeriesList.length, 3)}`,
              featuredSlideDirection
                ? `featured-slide-${featuredSlideDirection}`
                : '',
            ]"
          >
            <!-- 左箭頭 -->
            <button
              v-if="featuredSeriesList.length > 1"
              type="button"
              class="featured-nav featured-nav-prev"
              aria-label="上一個熱門系列"
              @click="previousFeaturedPage"
            >
              <b-icon icon="chevron-left"></b-icon>
            </button>

            <!-- 舞台 -->
            <div class="featured-series-stage">
              <article
                v-for="series in visibleFeaturedSeriesList"
                :key="`featured-${series.Id}-${series._featuredPosition}`"
                class="featured-series-card"
                :class="`featured-series-card-${series._featuredPosition}`"
                role="button"
                tabindex="0"
                @click="goPrograms(series)"
                @keydown.enter="goPrograms(series)"
                @keydown.space.prevent="goPrograms(series)"
              >
                <img
                  :src="getSeriesCover(series)"
                  :alt="series.Name"
                  @error="setDefaultCover"
                />

                <div class="featured-series-shade"></div>

                <span class="featured-series-badge">
                  熱門推薦
                </span>

                <div class="featured-series-content">
                  <h3>{{ series.Name }}</h3>

                  <p
                    v-if="series.Description"
                    class="featured-series-description"
                    v-html="series.Description"
                  >
                  </p>

                  <div
                    v-if="
                      series.DisplayPrice !== null &&
                        series.DisplayPrice !== undefined
                    "
                    class="featured-series-price"
                  >
                    <span
                      v-if="isSeriesEarlyBirdActive(series)"
                      class="featured-price-label"
                    >
                      早鳥優惠
                    </span>

                    <strong>
                      NT$
                      {{
                        Number(seriesDisplayPrice(series) || 0).toLocaleString(
                          "zh-TW",
                        )
                      }}
                    </strong>

                    <span v-if="series.DisplayPriceFrom">
                      起
                    </span>

                    <span
                      v-if="isSeriesEarlyBirdActive(series)"
                      class="featured-price-deadline"
                    >
                      截止 {{ formatDate(series.DisplayEarlyBirdEnd) }}
                    </span>
                  </div>

                  <div class="featured-series-meta">
                    <span
                      class="featured-series-status"
                      :class="{ 'is-open': series.DisplayCanApply }"
                    >
                      <span class="featured-series-status-dot" aria-hidden="true"></span>
                      <span v-if="series.DisplayCanApply" class="admission-status-label">招生中</span>
                      <span v-else>規劃中</span>
                    </span>
                    <div
                      v-if="
                        series.DisplayAllGrades ||
                          series.DisplayGradeMin ||
                          series.DisplayGradeMax
                      "
                      class="featured-series-grade"
                    >
                      <b-icon icon="people-fill" class="mr-1" aria-hidden="true"></b-icon>
                      <template v-if="series.DisplayAllGrades">不限年齡</template>
                      <template
                        v-else-if="
                          series.DisplayGradeMin && series.DisplayGradeMax
                        "
                      >
                        適合 {{ series.DisplayGradeMin }}－{{
                          series.DisplayGradeMax
                        }} 年級
                      </template>
                      <template v-else-if="series.DisplayGradeMin">
                        適合 {{ series.DisplayGradeMin }} 年級以上
                      </template>
                      <template v-else>
                        適合 {{ series.DisplayGradeMax }} 年級以下
                      </template>
                    </div>
                  </div>
                </div>
              </article>
            </div>

            <!-- 右箭頭 -->
            <button
              v-if="featuredSeriesList.length > 1"
              type="button"
              class="featured-nav featured-nav-next"
              aria-label="下一個熱門系列"
              @click="nextFeaturedPage"
            >
              <b-icon icon="chevron-right"></b-icon>
            </button>
          </div>

          <!-- Pagination -->
          <div v-if="featuredSeriesList.length > 1" class="featured-pagination">
            <button
              v-for="(series, index) in featuredSeriesList"
              :key="`featured-dot-${series.Id}`"
              type="button"
              class="featured-pagination-dot"
              :class="{ active: featuredPage === index }"
              :aria-label="`切換到第 ${index + 1} 個熱門系列`"
              @click="goFeaturedPage(index)"
            ></button>
          </div>
        </section>

        <section
          v-if="earlyBirdSeriesList.length > 0"
          class="early-bird-section"
        >
          <div class="early-bird-heading">
            <div>
              <span class="section-kicker">LIMITED TIME</span>
              <h2>早鳥優惠進行中 <span class="section-count">{{ earlyBirdSeriesList.length }}</span></h2>
              <p class="featured-description">
                把握優惠期限，提早為孩子安排精彩的學習旅程
              </p>
            </div>
            <span class="early-bird-heading-badge">
              <b-icon icon="alarm"></b-icon>
              限時優惠
            </span>
          </div>

          <splide
            class="early-bird-carousel"
            :slides="earlyBirdSeriesList"
            :options="earlyBirdOptions"
            aria-label="限時優惠營隊"
          >
            <splide-slide
              v-for="series in earlyBirdSeriesList"
              :key="`early-bird-${series.Id}`"
            >
              <article class="early-bird-card" @click="goPrograms(series)">
                <div class="early-bird-cover">
                  <img
                    :src="getSeriesCover(series)"
                    :alt="series.Name"
                    @error="setDefaultCover"
                  />
                </div>
                <div class="early-bird-card-body">
                  <h3>{{ series.Name }}</h3>
                  <div class="early-bird-card-price">
                    <strong>NT$ {{ Number(series.DisplayEarlyBirdPrice || 0).toLocaleString("zh-TW") }}</strong>
                    <span>起</span>
                  </div>
                  <small>優惠至 {{ formatDate(series.DisplayEarlyBirdEnd) }}</small>
                  <b-button
                    block
                    size="sm"
                    variant="outline-primary"
                    class="early-bird-card-button"
                    @click.stop="goPrograms(series)"
                  >
                    查看課程 <b-icon icon="arrow-right"></b-icon>
                  </b-button>
                </div>
              </article>
            </splide-slide>
          </splide>
        </section>

        <section v-if="regularSeriesList.length > 0" class="all-series-section">
          <div class="all-series-heading">
            <span class="section-kicker">ALL SERIES</span>
            <h2>探索全部系列 <span class="section-count">{{ regularSeriesList.length }}</span></h2>
          </div>

          <b-row class="camp-series-list">
            <b-col
              v-for="series in regularSeriesList"
              :key="series.Id"
              cols="12"
              md="6"
              lg="4"
              class="mb-4"
            >
              <b-card
                no-body
                class="camp-series-card"
                :class="{ 'has-early-bird': isSeriesEarlyBirdActive(series) }"
                @click="goPrograms(series)"
              >
                <figure class="camp-series-cover mb-0">
                  <b-card-img
                    :src="getSeriesCover(series)"
                    :alt="series.Name"
                    @error="setDefaultCover"
                  ></b-card-img>
                  <div class="series-card-cover-tags">
                    <b-badge
                      :variant="series.DisplayCanApply ? 'success' : 'secondary'"
                      class="camp-apply-status"
                    >
                      <span v-if="series.DisplayCanApply" class="admission-status-label">招生中</span>
                      <span v-else>規劃中</span>
                    </b-badge>
                    <span v-if="isSeriesEarlyBirdActive(series)" class="series-card-highlight">
                      <b-icon icon="alarm" class="mr-1"></b-icon>
                      早鳥優惠
                    </span>
                  </div>
                  <span v-if="isSeriesEarlyBirdActive(series)" class="series-deadline">
                    <b-icon icon="alarm" class="mr-1"></b-icon>
                    優惠至 {{ formatDate(series.DisplayEarlyBirdEnd) }}
                  </span>
                </figure>

                <b-card-body>
                  <b-card-title title-tag="h3" class="series-title mb-3">
                    {{ series.Name }}
                  </b-card-title>
                  <div class="series-meta">
                    <div
                      v-if="
                        series.DisplayAllGrades ||
                          series.DisplayGradeMin ||
                          series.DisplayGradeMax
                      "
                      class="series-grade"
                    >
                      <b-icon icon="people-fill" class="mr-1" aria-hidden="true"></b-icon>
                      <template v-if="series.DisplayAllGrades">不限年齡</template>
                      <template
                        v-else-if="
                          series.DisplayGradeMin && series.DisplayGradeMax
                        "
                      >
                        適合 {{ series.DisplayGradeMin }}－{{
                          series.DisplayGradeMax
                        }}
                        年級
                      </template>
                      <template v-else-if="series.DisplayGradeMin">
                        適合 {{ series.DisplayGradeMin }} 年級以上
                      </template>
                      <template v-else>
                        適合 {{ series.DisplayGradeMax }} 年級以下
                      </template>
                    </div>
                    <div
                      v-if="
                        series.DisplayPrice !== null &&
                          series.DisplayPrice !== undefined
                      "
                      :class="
                        isSeriesEarlyBirdActive(series)
                          ? 'series-price early-bird'
                          : 'series-price'
                      "
                    >
                      <span
                        v-if="isSeriesEarlyBirdActive(series)"
                        class="series-price-label"
                        >優惠價</span
                      >
                      <span class="series-price-currency">NT$</span>
                      <span class="series-price-number">
                        {{
                          Number(
                            seriesDisplayPrice(series) || 0,
                          ).toLocaleString("zh-TW")
                        }}
                      </span>
                      <span class="series-price-unit">
                        元{{ series.DisplayPriceFrom ? "起" : "" }}
                      </span>
                    </div>
                  </div>
                  <div class="series-card-footer">
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      class="series-card-action"
                      @click.stop="goPrograms(series)"
                    >
                      查看課程 <b-icon icon="arrow-right"></b-icon>
                    </b-button>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
        </section>
      </template>
    </b-container>

    <div class="camp-mobile-actions d-md-none">
      <b-button variant="light" @click="scrollToDiscovery">
        <b-icon icon="sliders" class="mr-1"></b-icon>
        篩選營隊
      </b-button>
      <b-button variant="primary" @click="_go('./campOverview')">
        <b-icon icon="grid-3x3-gap" class="mr-1"></b-icon>
        課程總覽
      </b-button>
    </div>

    <b-modal
      id="modal-camp-refund-policy"
      title="退費規則"
      size="lg"
      centered
      hide-footer
    >
      <div class="refund-policy-modal-intro">
        <b-icon icon="info-circle" aria-hidden="true"></b-icon>
        <span>請於報名前詳閱以下規則。</span>
      </div>
      <div
        class="refund-policy-content"
        v-html="refundPolicy ? refundPolicy.Content : ''"
      ></div>
      <div class="refund-policy-modal-actions">
        <b-button
          variant="primary"
          @click="$bvModal.hide('modal-camp-refund-policy')"
        >
          我知道了
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Splide, SplideSlide } from "@splidejs/vue-splide";
import "@splidejs/splide/dist/css/themes/splide-default.min.css";
import {
  apiGetCampPhoto,
  apiGetCampSeries,
  apiGetCampSeriesTypeSelector,
  apiGetInformation,
} from "@/api/API.js";

export default {
  name: "Camps",
  components: { Splide, SplideSlide },
  data() {
    return {
      seriesList: [],
      bannerList: [],
      typeList: [],
      refundPolicy: null,
      earlyBirdOptions: {
        perPage: 4,
        perMove: 1,
        gap: "2rem",
        arrows: true,
        pagination: false,
        autoplay: false,
        i18n: { prev: "上一個早鳥優惠營隊", next: "下一個早鳥優惠營隊" },
        breakpoints: {
          991: { perPage: 2, gap: "1.5rem" },
          767: { perPage: 1, gap: "1rem" },
        },
      },
      featuredPage: 0,
      featuredSlideDirection: "",
      featuredSlideTimer: null,
      loading: false,
      Param: {
        SortMode: "",
        Type: "",
        ApplyStatus: "",
      },
      applyStatusOptions: [
        { value: "", text: "全部狀態" },
        { value: "open", text: "招生中" },
        { value: "planning", text: "規劃中" },
      ],
      sortOptions: [
        { value: "", text: "預設排序" },
        { value: "priceAsc", text: "價格由低到高" },
        { value: "priceDesc", text: "價格由高到低" },
        { value: "createDesc", text: "由新到舊" },
      ],
    };
  },
  computed: {
    typeOptions() {
      return [{ value: "", text: "全部" }, ...this.typeList];
    },
    featuredSeriesList() {
      return this.seriesList.filter((series) => series.Mark === true);
    },
    featuredPageCount() {
      return this.featuredSeriesList.length;
    },
    earlyBirdSeriesList() {
      return this.seriesList.filter((series) => this.isSeriesEarlyBirdActive(series));
    },
    visibleFeaturedSeriesList() {
      const list = this.featuredSeriesList;
      const count = list.length;

      if (!count) {
        return [];
      }

      // 1 張：只有中央主卡
      if (count === 1) {
        return [
          {
            ...list[0],
            _featuredPosition: "center",
          },
        ];
      }

      // 2 張：主卡 + 右側預覽
      if (count === 2) {
        const currentIndex = this.featuredPage % count;
        const nextIndex = (currentIndex + 1) % count;

        return [
          {
            ...list[currentIndex],
            _featuredPosition: "center",
          },
          {
            ...list[nextIndex],
            _featuredPosition: "right",
          },
        ];
      }

      // 3 張以上：左 + 中 + 右
      const currentIndex = this.featuredPage % count;
      const prevIndex = (currentIndex - 1 + count) % count;
      const nextIndex = (currentIndex + 1) % count;

      return [
        {
          ...list[prevIndex],
          _featuredPosition: "left",
        },
        {
          ...list[currentIndex],
          _featuredPosition: "center",
        },
        {
          ...list[nextIndex],
          _featuredPosition: "right",
        },
      ];
    },
    regularSeriesList() {
      return this.seriesList;
    },
    canShowRefundPolicy() {
      return (
        this.refundPolicy &&
        this.refundPolicy.Enable === true &&
        this.hasRefundPolicyContent(this.refundPolicy.Content)
      );
    },
  },
  mounted() {
    this.getTypeList();
    this.getSeriesList();
    this.getBannerList();
    this.getRefundPolicy();
  },
  beforeDestroy() {
    if (this.featuredSlideTimer) {
      clearTimeout(this.featuredSlideTimer);
    }
  },
  methods: {
    selectApplyStatus(status) {
      this.Param.ApplyStatus = status;
      this.featuredPage = 0;
      this.getSeriesList();
    },
    scrollToDiscovery() {
      const discovery = this.$el.querySelector(".camp-discovery-panel");
      if (discovery) {
        discovery.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    getBannerList() {
      apiGetCampPhoto({
        Search: {},
        Sort: { Sort: "asc" },
        Paging: { pageNumber: 1, pageSize: 99 },
      })
        .then((res) => {
          this.bannerList = Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {
          this.bannerList = [];
        });
    },
    bannerUrl(banner) {
      return this.coverUrl(banner.Path, "_cover", banner.Id);
    },
    openBannerLink(banner) {
      if (!banner || !banner.Link) return;
      if (banner.Target === "_blank") {
        window.open(banner.Link, "_blank", "noopener,noreferrer");
        return;
      }
      window.location.href = banner.Link;
    },
    getRefundPolicy() {
      apiGetInformation("camp_refund_policy")
        .then((res) => {
          this.refundPolicy = res.data || null;
        })
        .catch(() => {
          this.refundPolicy = null;
        });
    },
    hasRefundPolicyContent(content) {
      return (
        String(content || "")
          .replace(/<[^>]*>/g, "")
          .replace(/&nbsp;|&#160;/gi, "")
          .trim().length > 0
      );
    },
    getTypeList() {
      apiGetCampSeriesTypeSelector()
        .then((res) => {
          this.typeList = Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {
          this.typeList = [];
        });
    },
    getSeriesList() {
      this.loading = true;
      const search = {};
      if (this.Param.Type) {
        search.type = this.Param.Type;
      }
      if (this.Param.ApplyStatus) {
        search.applyStatus = this.Param.ApplyStatus;
      }

      apiGetCampSeries({
        Search: search,
        Sort: this.buildSort(),
        Paging: {
          pageNumber: 1,
          pageSize: 9999,
        },
      })
        .then((res) => {
          this.seriesList = Array.isArray(res.data) ? res.data : [];
          this.featuredPage = 0;
        })
        .catch(() => {
          this.seriesList = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildSort() {
      switch (this.Param.SortMode) {
        case "priceAsc":
          return { Price: "asc" };
        case "priceDesc":
          return { Price: "desc" };
        case "createDesc":
          return { Create: "desc" };
        default:
          return {};
      }
    },
    goFeaturedPage(index) {
      const count = this.featuredSeriesList.length;

      if (
        count <= 1 ||
        index === this.featuredPage ||
        this.featuredSlideDirection
      ) {
        return;
      }

      const current = this.featuredPage;

      /*
       * 判斷 pagination 要往哪個方向移動。
       */
      const forwardDistance = (index - current + count) % count;

      const backwardDistance = (current - index + count) % count;

      this.startFeaturedSlide(
        forwardDistance <= backwardDistance ? "next" : "prev",
      );

      this.featuredPage = index;
    },
    onSortChange() {
      this.getSeriesList();
    },
    selectType(type) {
      this.Param.Type = type;
      this.getSeriesList();
    },
    previousFeaturedPage() {
      const count = this.featuredSeriesList.length;

      if (count <= 1 || this.featuredSlideDirection) {
        return;
      }

      this.startFeaturedSlide("prev");

      this.featuredPage = (this.featuredPage - 1 + count) % count;
    },

    nextFeaturedPage() {
      const count = this.featuredSeriesList.length;

      if (count <= 1 || this.featuredSlideDirection) {
        return;
      }

      this.startFeaturedSlide("next");

      this.featuredPage = (this.featuredPage + 1) % count;
    },
    startFeaturedSlide(direction) {
      this.featuredSlideDirection = direction;

      window.clearTimeout(this.featuredSlideTimer);

      this.featuredSlideTimer = window.setTimeout(() => {
        this.featuredSlideDirection = "";
      }, 420);
    },
    goPrograms(series) {
      if (!series || !series.Id) {
        return;
      }

      this._go(`./campPrograms?id=${series.Id}`);
    },
    fileBaseUrl() {
      return this.$store.state.FilePath;
    },
    getSeriesCover(series) {
      if (!series || !series.Id || !series.Cover) {
        return "./asset/no-image.png";
      }

      return this.coverUrl(series.Cover, "_camp_series", series.Id);
    },
    coverUrl(cover, folder, id) {
      if (cover.startsWith("/")) {
        return `${this.fileBaseUrl()}files${cover}`;
      }

      return `${this.fileBaseUrl()}files/${folder}/${id}/${encodeURIComponent(
        cover,
      )}`;
    },
    setDefaultCover(event) {
      event.target.src = "./asset/no-image.png";
    },
    seriesPriceText(series) {
      const suffix = series.DisplayPriceFrom ? "起" : "";
      return `${Number(series.DisplayPrice || 0).toLocaleString(
        "zh-TW",
      )} 元${suffix}`;
    },
    seriesDisplayPrice(series) {
      return this.isSeriesEarlyBirdActive(series)
        ? series.DisplayEarlyBirdPrice
        : series.DisplayPrice;
    },
    isSeriesEarlyBirdActive(series) {
      if (!series || series.DisplayEarlyBird !== true) return false;
      if (
        series.DisplayEarlyBirdPrice === null ||
        series.DisplayEarlyBirdPrice === undefined
      ) {
        return false;
      }

      const endDate = this.parseDisplayDate(series.DisplayEarlyBirdEnd);
      if (!endDate) return false;

      return endDate >= new Date();
    },
    formatDate(value) {
      const date = this.parseDisplayDate(value);
      if (!date) return "未設定";

      return `${date.getMonth() + 1}/${date.getDate()}`;
    },
    parseDisplayDate(value) {
      if (!value) return null;
      const text = String(value);
      const match = text.match(
        /^(\d{4})-(\d{2})-(\d{2})T?(\d{2})?:?(\d{2})?:?(\d{2})?/,
      );
      if (match) {
        return new Date(
          Number(match[1]),
          Number(match[2]) - 1,
          Number(match[3]),
          Number(match[4] || 0),
          Number(match[5] || 0),
          Number(match[6] || 0),
        );
      }

      const date = new Date(text);
      return Number.isNaN(date.getTime()) ? null : date;
    },
  },
};
</script>

<style lang="scss" scoped>
/* =========================================================
   Camps Page
   ========================================================= */

.camps-page {
  min-height: 100vh;
  max-width: 100%;
  overflow-x: hidden;
  background-color: #f7f9fb;
}

/* =========================================================
   Banner
   ========================================================= */

.camp-banner {
  background-color: #f0f9ff;
}

.camp-banner ::v-deep .carousel-control-prev,
.camp-banner ::v-deep .carousel-control-next {
  top: 50%;
  bottom: auto;

  width: 48px;
  height: 48px;

  border-radius: 50%;

  background: rgba(26, 26, 26, 0.72);

  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.28);

  opacity: 1;

  transform: translateY(-50%);

  transition: background-color 0.2s ease;
}

.camp-banner ::v-deep .carousel-control-prev {
  left: 1rem;
}

.camp-banner ::v-deep .carousel-control-next {
  right: 1rem;
}

.camp-banner ::v-deep .carousel-control-prev:hover,
.camp-banner ::v-deep .carousel-control-next:hover,
.camp-banner ::v-deep .carousel-control-prev:focus,
.camp-banner ::v-deep .carousel-control-next:focus {
  background: #1a1a1a;
}

/* =========================================================
   Banner Frame
   ========================================================= */

.camp-banner-frame {
  position: relative;

  display: flex;

  width: 100%;
  aspect-ratio: 1440 / 452;

  overflow: hidden;

  align-items: center;
  justify-content: center;

  isolation: isolate;

  background-color: #dceaf2;
}

.camp-banner-frame.has-link {
  cursor: pointer;
}

.camp-banner-backdrop {
  position: absolute;
  z-index: 0;

  inset: -32px;

  background-position: center;
  background-size: cover;

  filter: blur(24px);

  opacity: 0.72;

  transform: scale(1.08);
}

.camp-banner-backdrop::after {
  position: absolute;

  inset: 0;

  background: rgba(0, 45, 72, 0.14);

  content: "";
}

.camp-banner-image {
  position: relative;
  z-index: 1;

  width: 100%;
  height: 100%;

  object-fit: contain;
  object-position: center;
}

.camp-banner-empty {
  display: flex;

  min-height: 220px;

  align-items: center;

  color: #004e7a;

  background: linear-gradient(135deg, #f0f9ff, #d9effc);

  font-size: 2rem;
  font-weight: 700;
}

/* =========================================================
   Section Common
   ========================================================= */

.section-kicker {
  display: block;

  margin-bottom: 0.35rem;

  color: #0782ca;

  font-size: 0.82rem;
  font-weight: 700;
}

/* =========================================================
   Main Series Header
   ========================================================= */

.series-header {
  display: flex;

  align-items: flex-end;
  justify-content: space-between;

  gap: 1rem;

  margin-bottom: 1.25rem;
}

.series-header h2 {
  margin: 0;

  color: #14233b;

  font-size: 1.8rem;
  font-weight: 800;
}

.section-description {
  margin: 0.45rem 0 0;

  color: #7a8798;

  font-size: 0.92rem;
}

/* =========================================================
   Header Actions
   ========================================================= */

.series-header-actions {
  display: flex;

  flex: 0 0 auto;

  align-items: center;

  gap: 0.5rem;
}

.overview-btn {
  min-width: 88px;

  padding: 0.5rem 1rem;

  border-radius: 6px;

  font-size: 0.9rem;
  font-weight: 600;

  &:hover {
    color: #fff;
  }
}

.refund-policy-btn {
  min-width: 106px;

  padding: 0.5rem 1rem;

  border-radius: 6px;

  font-size: 0.9rem;
  font-weight: 600;

  .b-icon {
    margin-right: 0.35rem;
  }
}

/* =========================================================
   Discovery Panel
   ========================================================= */

.camp-discovery-panel {
  margin-bottom: 2.5rem;
  padding: 1.5rem;
  border: 1px solid #e0eaf1;
  border-radius: 18px;
  background: linear-gradient(135deg, #ffffff 0%, #f3faff 100%);
  box-shadow: 0 12px 30px rgba(38, 62, 88, 0.08);
}

.camp-discovery-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.camp-discovery-heading h2 {
  margin: 0;
  color: #14233b;
  font-size: 1.8rem;
  font-weight: 800;
}

.camp-filter-block {
  flex: 1 1 auto;
  min-width: 0;
}

.camp-filter-caption {
  display: block;
  margin-bottom: 0.45rem;
  color: #617487;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.camp-discovery-panel .series-toolbar {
  margin-bottom: 0;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
}

.camp-discovery-panel .camp-status-block {
  flex: 0 1 auto;
}

.camp-discovery-panel .camp-status-block .camp-type-filter {
  flex: 0 0 auto;
}

.camp-discovery-panel .sort-box {
  flex: 0 0 170px;
}

.camp-discovery-panel .sort-box .custom-select {
  width: 100%;
}

/* =========================================================
   Refund Modal
   ========================================================= */

.refund-policy-modal-intro {
  display: flex;

  align-items: center;

  gap: 0.5rem;

  margin-bottom: 1rem;

  padding: 0.75rem 0.9rem;

  border-left: 3px solid #087bc1;

  background: #f2f9fd;

  color: #245b7a;

  font-size: 0.9rem;
}

.refund-policy-content {
  color: #344054;

  line-height: 1.8;

  :deep(img) {
    max-width: 100%;
    height: auto;
  }
}

.refund-policy-modal-actions {
  display: flex;

  justify-content: flex-end;

  margin-top: 1.5rem;

  padding-top: 1rem;

  border-top: 1px solid #e7edf3;
}

/* =========================================================
   Toolbar
   ========================================================= */

.series-toolbar {
  display: flex;
  flex-wrap: wrap;

  align-items: center;
  justify-content: space-between;

  gap: 1rem;

  margin-bottom: 2rem;

  padding: 0.9rem 1rem;

  border: 1px solid #e7edf3;
  border-radius: 14px;

  background: linear-gradient(135deg, #ffffff 0%, #f8fbfd 100%);

  box-shadow: 0 8px 24px rgba(38, 62, 88, 0.07);
}

/* =========================================================
   Type Filter
   ========================================================= */

.camp-type-filter {
  display: flex;

  flex: 1;
  flex-wrap: wrap;

  align-items: center;

  gap: 0.65rem;
}

.camp-status-filter {
  flex: 0 0 auto;
  width: 100%;
}

.type-btn {
  min-width: 82px;
  height: 38px;

  padding: 0 1.1rem;

  border: 1px solid #dfe4ea;
  border-radius: 9px;

  background: #fff;

  color: #344054;

  font-size: 0.9rem;
  font-weight: 600;

  cursor: pointer;

  transition: background-color 0.2s ease, border-color 0.2s ease,
    color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: #087bc1;

    background: #f5fbff;

    color: #087bc1;
  }

  &.active {
    border-color: #087bc1;

    background: #087bc1;

    color: #fff;

    box-shadow: 0 5px 12px rgba(8, 123, 193, 0.2);
  }
}

/* =========================================================
   Sort
   ========================================================= */

.sort-box {
  flex: 0 0 auto;

  .custom-select {
    width: 165px;
    height: 38px;

    border: 1px solid #dfe4ea;
    border-radius: 8px;

    background-color: #fff;

    color: #344054;

    font-size: 0.88rem;
    font-weight: 500;

    box-shadow: none;

    &:focus {
      border-color: #087bc1;

      box-shadow: 0 0 0 3px rgba(8, 123, 193, 0.08);
    }
  }
}

/* =========================================================
   Loading
   ========================================================= */

.loading-block {
  display: flex;

  align-items: center;
  justify-content: center;

  min-height: 240px;
}

/* =========================================================
   Featured Series
   ========================================================= */

.featured-series-section {
  position: relative;

  margin-bottom: 4rem;

  padding: 1.5rem 1.5rem 1.75rem;
  border: 1px solid rgba(8, 123, 193, 0.1);
  border-radius: 22px;
  background: #f8fcff;
  box-shadow: 0 16px 38px rgba(20, 74, 108, 0.08);
}

.featured-section-heading,
.all-series-heading {
  margin-bottom: 1.25rem;

  h2 {
    margin: 0;

    color: #14233b;

    font-size: 1.8rem;
    font-weight: 800;
  }
}

.section-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-left: 0.4rem;
  padding: 0;
  border-radius: 50%;
  background: var(--primary);
  color: #fff;
  font-size: 0.58em;
  font-weight: 800;
  letter-spacing: 0.01em;
  vertical-align: middle;
  white-space: nowrap;
}

.featured-section-heading,
.all-series-heading {
  h2 {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.15rem;
  }
}

.featured-section-heading {
  position: relative;

  z-index: 2;

  margin-bottom: 1.5rem;
}

.featured-description {
  margin: 0.45rem 0 0;

  color: #7a8798;

  font-size: 0.92rem;
  line-height: 1.6;
}

/* =========================================================
   Featured Carousel
   ========================================================= */

.featured-series-carousel {
  position: relative;

  width: 100%;
  max-width: 1280px;

  margin: 0 auto;
}
/* =========================================================
   Featured：只有 1 張
   ========================================================= */

.featured-series-carousel.featured-count-1 {
  max-width: 820px;

  .featured-series-stage {
    height: 500px;
  }

  .featured-series-card-center {
    width: 88%;
    max-width: 720px;
  }
}

/* =========================================================
   Featured：只有 2 張
   ========================================================= */

.featured-series-carousel.featured-count-2 {
  max-width: 1280px;

  .featured-series-stage {
    display: flex;

    align-items: center;
    justify-content: flex-start;

    gap: 24px;

    height: 350px;

    padding: 0 65px;
  }

  .featured-series-card {
    position: relative;

    top: auto;
    left: auto;

    flex: 0 0 calc(50% - 12px);

    width: calc(50% - 12px);
    max-width: none;
    height: 310px;

    opacity: 1;

    filter: none;

    transform: none;
  }

  .featured-series-card-center,
  .featured-series-card-right {
    position: relative;

    top: auto;
    left: auto;

    height: 310px;

    opacity: 1;

    filter: none;

    transform: none;
  }

  .featured-series-card-right::after {
    display: none;
  }

  .featured-series-card-center:hover,
  .featured-series-card-right:hover {
    z-index: 2;

    opacity: 1;

    filter: none;

    transform: translateY(-5px);

    box-shadow: 0 24px 50px rgba(20, 35, 59, 0.18),
      0 6px 16px rgba(20, 35, 59, 0.08);
  }

  .featured-series-card-center:hover img,
  .featured-series-card-right:hover img {
    transform: scale(1.035);
  }

  .featured-nav-prev {
    left: 0;
  }

  .featured-nav-next {
    right: 0;
  }
}
/* =========================================================
   Featured Stage
   ========================================================= */

.featured-series-stage {
  position: relative;

  width: 100%;
  height: 400px;

  margin: 0 auto;

  overflow: hidden;
}

/* =========================================================
   Featured Card
   ========================================================= */

.featured-series-card {
  position: absolute;

  top: 50%;
  left: 50%;

  width: 56%;
  max-width: 720px;
  height: 345px;

  overflow: hidden;

  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 20px;

  background-color: #0b679f;

  box-shadow: 0 18px 45px rgba(20, 35, 59, 0.13),
    0 4px 12px rgba(20, 35, 59, 0.08);

  cursor: pointer;

  outline: none;

  touch-action: manipulation;

  transition: transform 0.38s cubic-bezier(0.2, 0.7, 0.2, 1), opacity 0.3s ease,
    filter 0.3s ease, box-shadow 0.3s ease;

  img {
    position: absolute;

    inset: 0;

    width: 100%;
    height: 100%;

    object-fit: cover;
    object-position: center;

    transition: transform 0.45s ease, filter 0.3s ease;
  }

  &:focus-visible {
    box-shadow: 0 0 0 4px rgba(8, 123, 193, 0.24),
      0 18px 45px rgba(20, 35, 59, 0.18);
  }
}

/* =========================================================
   Featured Center Card
   ========================================================= */

.featured-series-card-center {
  z-index: 3;

  height: 380px;

  opacity: 1;

  transform: translate(-50%, -50%) scale(1);
}

.featured-series-card-center:hover {
  z-index: 5;

  box-shadow: 0 28px 65px rgba(20, 35, 59, 0.2),
    0 8px 20px rgba(20, 35, 59, 0.1);

  transform: translate(-50%, calc(-50% - 5px)) scale(1.012);
}

.featured-series-card-center:hover img {
  transform: scale(1.035);
}

/* =========================================================
   Featured Left Card
   ========================================================= */

.featured-series-card-left {
  z-index: 1;

  opacity: 0.7;

  filter: saturate(0.75);

  transform: translate(-118%, -50%) scale(0.88);
}

.featured-series-card-left:hover {
  z-index: 2;

  opacity: 0.88;

  filter: saturate(0.9);

  transform: translate(-116%, -50%) scale(0.9);
}

/* =========================================================
   Featured Right Card
   ========================================================= */

.featured-series-card-right {
  z-index: 1;

  opacity: 0.7;

  filter: saturate(0.75);

  transform: translate(18%, -50%) scale(0.88);
}

.featured-series-card-right:hover {
  z-index: 2;

  opacity: 0.88;

  filter: saturate(0.9);

  transform: translate(16%, -50%) scale(0.9);
}

/* =========================================================
   Side Card Overlay
   ========================================================= */

.featured-series-card-left::after,
.featured-series-card-right::after {
  position: absolute;

  z-index: 4;

  inset: 0;

  background: rgba(15, 36, 55, 0.18);

  pointer-events: none;

  content: "";

  transition: background 0.25s ease;
}

.featured-series-card-left:hover::after,
.featured-series-card-right:hover::after {
  background: rgba(15, 36, 55, 0.08);
}

/* =========================================================
   Featured Shade
   ========================================================= */

.featured-series-shade {
  position: absolute;

  z-index: 1;

  inset: 0;

  background: linear-gradient(
    180deg,
    rgba(10, 22, 35, 0) 32%,
    rgba(10, 22, 35, 0.1) 48%,
    rgba(10, 22, 35, 0.92) 100%
  );

  pointer-events: none;
}

/* =========================================================
   Featured Badge
   ========================================================= */

.featured-series-badge {
  position: absolute;

  top: 1rem;
  left: 1rem;

  z-index: 5;

  display: inline-flex;

  align-items: center;

  padding: 0.38rem 0.8rem;

  border-radius: 999px;

  background: linear-gradient(135deg, #ffd45c, #f4c53e);

  color: #4e3a09;

  font-size: 0.78rem;
  font-weight: 800;

  box-shadow: 0 5px 14px rgba(91, 65, 5, 0.14);
}

/* =========================================================
   Featured Content
   ========================================================= */

.featured-series-content {
  position: absolute;

  right: 0;
  bottom: 0;
  left: 0;

  z-index: 3;

  padding: 1.35rem 1.45rem 1.25rem;

  color: #fff;

  h3 {
    margin: 0 0 0.55rem;

    color: #fff;

    font-size: 1.35rem;
    font-weight: 800;
    line-height: 1.3;

    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
  }
}

/* =========================================================
   Featured Price
   ========================================================= */

.featured-series-price {
  display: flex;

  flex-wrap: wrap;

  align-items: baseline;

  gap: 0.35rem;

  color: rgba(255, 255, 255, 0.88);

  font-size: 0.78rem;

  strong {
    color: #ffe58c;

    font-size: 1.15rem;
    font-weight: 800;

    letter-spacing: -0.01em;
  }
}

.featured-price-label {
  color: #ffe58c;

  font-weight: 700;
}

.featured-price-deadline {
  color: rgba(255, 255, 255, 0.78);
}

.featured-series-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.65rem;
}

.featured-series-status {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.74rem;
  font-weight: 700;
}

.featured-series-status.is-open {
  color: #b9f2c5;
}

.featured-series-status-dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: currentColor;
}

/* =========================================================
   Featured Grade
   ========================================================= */

.featured-series-grade {
  display: inline-flex;

  align-items: center;

  width: max-content;

  margin-top: 0;

  color: rgba(255, 255, 255, 0.78);

  font-size: 0.74rem;
  font-weight: 500;
}

/* =========================================================
   Featured Navigation
   ========================================================= */

.featured-nav {
  position: absolute;

  top: 50%;

  z-index: 10;

  display: flex;

  align-items: center;
  justify-content: center;

  width: 50px;
  height: 50px;

  padding: 0;

  border: 1px solid rgba(8, 123, 193, 0.1);
  border-radius: 50%;

  background: rgba(255, 255, 255, 0.94);

  color: #087bc1;

  box-shadow: 0 12px 30px rgba(20, 35, 59, 0.16);

  cursor: pointer;

  transform: translateY(-50%);

  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease,
    transform 0.2s ease;

  &:hover {
    background: #087bc1;

    color: #fff;

    box-shadow: 0 16px 36px rgba(8, 123, 193, 0.28);

    transform: translateY(-50%) scale(1.07);
  }

  &:focus-visible {
    outline: none;

    box-shadow: 0 0 0 4px rgba(8, 123, 193, 0.16),
      0 16px 36px rgba(8, 123, 193, 0.22);
  }
}

.featured-nav-prev {
  left: 4%;
}

.featured-nav-next {
  right: 4%;
}

/* =========================================================
   Featured Pagination
   ========================================================= */

.featured-pagination {
  display: flex;

  align-items: center;
  justify-content: center;

  gap: 0.5rem;

  margin-top: 0.85rem;
}

.featured-pagination-dot {
  width: 8px;
  height: 8px;

  padding: 0;

  border: 0;
  border-radius: 999px;

  background: #ccd7e2;

  cursor: pointer;

  transition: width 0.25s ease, background 0.25s ease, transform 0.25s ease;

  &.active {
    width: 24px;

    background: #087bc1;
  }

  &:hover {
    background: #5ca6d3;

    transform: scale(1.12);
  }

  &:focus-visible {
    outline: 2px solid rgba(8, 123, 193, 0.35);
    outline-offset: 3px;
  }
}

/* =========================================================
   Featured Animation
   ========================================================= */

.featured-count-3.featured-slide-next .featured-series-card-center {
  animation: featuredCenterNext 0.38s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.featured-count-3.featured-slide-prev .featured-series-card-center {
  animation: featuredCenterPrev 0.38s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.featured-count-2.featured-slide-next .featured-series-card,
.featured-count-2.featured-slide-prev .featured-series-card {
  animation: featuredPairFade 0.38s ease;
}

@keyframes featuredPairFade {
  from {
    opacity: 0.45;
  }
  to {
    opacity: 1;
  }
}

@keyframes featuredCenterNext {
  from {
    opacity: 0.75;

    transform: translate(-42%, -50%) scale(0.96);
  }

  to {
    opacity: 1;

    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes featuredCenterPrev {
  from {
    opacity: 0.75;

    transform: translate(-58%, -50%) scale(0.96);
  }

  to {
    opacity: 1;

    transform: translate(-50%, -50%) scale(1);
  }
}

/* =========================================================
   Early Bird Series
   ========================================================= */

.early-bird-section {
  margin-bottom: 3.5rem;
  padding: 1.4rem 1.5rem 0.75rem;
  border-radius: 18px;
  background: linear-gradient(135deg, #fff8e8 0%, #ffedc7 100%);
  box-shadow: 0 10px 28px rgba(167, 111, 37, 0.08);
  border: 1px solid rgba(192, 120, 29, 0.14);
}

.early-bird-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.early-bird-heading h2 {
  margin: 0;
  color: #5c3d15;
  font-size: 1.55rem;
  font-weight: 800;
}

.early-bird-heading .section-kicker {
  color: #c0781d;
}

.early-bird-section .section-count {
  margin-left: 0.3rem;
  background: #c0781d;
  color: #fffaf0;
}

.early-bird-heading-badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.35rem;
  padding: 0.42rem 0.7rem;
  border-radius: 999px;
  background: #f3d06b;
  color: #75480a;
  font-size: 0.78rem;
  font-weight: 800;
  white-space: nowrap;
}

.early-bird-carousel {
  padding: 0 8px 1.5rem;
}

.early-bird-carousel ::v-deep .splide__list {
  align-items: stretch;
}

.early-bird-carousel ::v-deep .splide__slide {
  display: flex;
  height: auto;
}

.early-bird-carousel ::v-deep .splide__track {
  padding-top: 8px;
  padding-bottom: 16px;
}

.early-bird-carousel ::v-deep .splide__arrow {
  z-index: 2;
  width: 36px;
  height: 36px;
  background: rgb(238 187 56 / 78%);
  border: 1px solid #d98a2b;
  opacity: 0.76;
  box-shadow: 0 3px 10px rgba(139, 91, 25, 0.12);
  transition: opacity 0.2s ease, background-color 0.2s ease;
}

.early-bird-carousel ::v-deep .splide__arrow:hover {
  background: rgb(238 187 56 / 96%);
  opacity: 1;
}

.early-bird-carousel ::v-deep .splide__arrow:disabled {
  opacity: 0.35;
  cursor: default;
}

.early-bird-carousel ::v-deep .splide__arrow--prev {
  left: -0.15rem;
}

.early-bird-carousel ::v-deep .splide__arrow--next {
  right: -0.15rem;
}

.early-bird-card {
  flex: 1 1 auto;
  height: 100%;
  margin: 0 8px;
  overflow: hidden;
  border: 1px solid rgba(192, 120, 29, 0.16);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(139, 91, 25, 0.08);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.early-bird-card:hover {
  box-shadow: 0 16px 30px rgba(139, 91, 25, 0.15);
  transform: translateY(-4px);
}

.early-bird-card-button {
  border-color: #d98a2b;
  color: #b86b18;
}

.early-bird-card-button:hover {
  border-color: #d98a2b;
  background: #d98a2b;
  color: #fff;
}

.early-bird-cover {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #f7ead2;
}

.early-bird-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.35s ease;
}

.early-bird-card:hover .early-bird-cover img {
  transform: scale(1.05);
}

.early-bird-card-body {
  padding: 0.9rem 1rem 1rem;
}

.early-bird-card-body h3 {
  overflow: hidden;
  margin: 0 0 0.6rem;
  color: #263746;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.early-bird-card-price {
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  color: #c43a31;
}

.early-bird-card-price strong {
  font-size: 1.2rem;
}

.early-bird-card-price span,
.early-bird-card-body > small {
  color: #8d6a45;
  font-size: 0.76rem;
}

.early-bird-card-button {
  margin-top: 0.8rem;
  border-radius: 7px;
  font-weight: 700;
}

/* =========================================================
   All Series
   ========================================================= */

.all-series-section {
  position: relative;
}

.all-series-heading {
  margin-bottom: 1.25rem;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid #e7eef3;
}

/* =========================================================
   Regular Series Card
   ========================================================= */

.camp-series-card {
  position: relative;

  height: 100%;

  overflow: hidden;

  border: 1px solid rgba(31, 41, 55, 0.1);
  border-radius: 12px;

  background-color: #fff;

  box-shadow: 0 14px 34px rgba(29, 45, 53, 0.07);

  cursor: pointer;

  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: var(--primary);

    box-shadow: 0 20px 44px rgba(0, 122, 194, 0.16);

    transform: translateY(-4px);
  }

  &:focus-within {
    border-color: var(--primary);

    box-shadow: 0 0 0 4px rgba(8, 123, 193, 0.1),
      0 20px 44px rgba(0, 122, 194, 0.14);
  }

  .card-body {
    display: flex;
    flex-direction: column;
    min-height: 220px;
    padding: 1.2rem 1.25rem 1.35rem;
  }
}

.camp-series-card.has-early-bird {
  border-color: rgba(214, 55, 69, 0.22);
}

/* =========================================================
   Regular Series Cover
   ========================================================= */

.camp-series-cover {
  position: relative;

  width: 100%;

  aspect-ratio: 16 / 9;

  overflow: hidden;

  background-color: #f4f6f8;

  .card-img {
    width: 100%;
    height: 100%;

    border-top-left-radius: 12px;
    border-top-right-radius: 12px;

    object-fit: cover;
    object-position: center;

    transition: transform 0.35s ease;
  }
}

.camp-series-card:hover .camp-series-cover .card-img {
  transform: scale(1.04);
}

/* =========================================================
   Regular Series Title
   ========================================================= */

.card-title {
  line-height: 1.35;
}

.series-title {
  overflow: hidden;
  min-height: 1.35em;
  margin-bottom: 0.4rem !important;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.series-card-cover-tags {
  position: absolute;
  top: 0.7rem;
  left: 0.7rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
  z-index: 1;
}

.series-card-cover-tags .camp-apply-status,
.series-card-cover-tags .series-card-highlight {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  box-sizing: border-box;
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  line-height: 1.2;
}

.series-card-cover-tags .camp-apply-status.badge-success {
  background-color: #28a745;
  border-color: #28a745;
}

.series-card-cover-tags .camp-apply-status.badge-secondary {
  background-color: #4b6075;
  border-color: #4b6075;
}

.admission-status-label {
  display: inline-block;
  animation: admission-status-wave 2.2s ease-in-out infinite;
  transform-origin: center bottom;
}

@keyframes admission-status-wave {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  12% { transform: translateY(-1px) rotate(-1deg); }
  24% { transform: translateY(0) rotate(1deg); }
  36% { transform: translateY(-1px) rotate(-1deg); }
  48% { transform: translateY(0) rotate(0deg); }
}

.series-card-highlight {
  display: inline-flex;
  align-items: center;
  background: #c0781d;
  color: #fff;
  font-weight: 800;
}

/* =========================================================
   Regular Series Meta
   ========================================================= */

.series-meta {
  display: flex;

  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-start;

  gap: 0.35rem;
  min-height: 58px;
}

/* =========================================================
   Regular Series Price
   ========================================================= */

.series-price {
  display: inline-flex;

  margin-top: 0.3rem;

  flex-shrink: 0;
  flex-wrap: wrap;

  align-items: baseline;

  gap: 0.25rem;

  padding: 0.4rem 0.7rem;

  border: 1px solid rgba(196, 58, 49, 0.16);
  border-radius: 8px;

  background: linear-gradient(
    135deg,
    rgba(255, 245, 243, 0.95),
    rgba(255, 250, 248, 0.95)
  );

  color: #c43a31;

  white-space: nowrap;

  .series-price-currency {
    color: #b56a63;

    font-size: 0.7rem;
    font-weight: 700;
  }

  .series-price-number {
    font-size: 1.35rem;
    font-weight: 800;
    line-height: 1;

    letter-spacing: -0.02em;
  }

  .series-price-unit {
    color: #c43a31;

    font-size: 0.75rem;
    font-weight: 700;
  }

  .series-price-label {
    font-size: 0.75rem;
    font-weight: 700;
  }

  .series-price-label {
    color: #b84616;
  }

}

.series-price.early-bird {
  align-items: center;

  border-color: rgba(214, 55, 69, 0.22);
  border-left: 4px solid #d63745;

  background: #fff7f2;
}

/* =========================================================
   Regular Series Grade
   ========================================================= */

.series-grade {
  display: flex;

  flex-shrink: 0;

  align-items: center;

  width: max-content;
  margin: 0;
  color: #7a8794;
  font-size: 0.78rem;
  font-weight: 500;
}

.series-card-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
  min-height: 42px;
  margin-top: auto;
  padding-top: 0.9rem;
  border-top: 1px solid #edf1f4;
}

.series-deadline {
  position: absolute;
  right: 0;
  bottom: 15px;
  z-index: 1;
  overflow: hidden;
  max-width: calc(100% - 1rem);
  padding: 0.3rem 0.65rem;
  border-radius: 4px 0 0 4px;
  background: #fff0c9;
  color: #9a5b12;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.74rem;
  font-weight: 700;
}

.series-card-action {
  flex-shrink: 0;
  border-radius: 7px;
  font-weight: 700;
}

.camp-mobile-actions {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 30;
  display: flex;
  gap: 0.6rem;
  padding: 0.65rem 1rem calc(0.65rem + env(safe-area-inset-bottom));
  border-top: 1px solid #dfe8ef;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 -8px 24px rgba(31, 60, 82, 0.12);
  backdrop-filter: blur(10px);
}

.camp-mobile-actions .btn {
  flex: 1;
  border-radius: 9px;
  font-weight: 700;
}

/* =========================================================
   Tablet
   ========================================================= */

@media (min-width: 768px) and (max-width: 991.98px) {
  .featured-series-stage {
    height: 350px;
  }

  .featured-series-card {
    width: 65%;
    height: 305px;
  }

  .featured-series-card-center {
    height: 330px;
  }

  .featured-series-card-left {
    transform: translate(-112%, -50%) scale(0.86);
  }

  .featured-series-card-right {
    transform: translate(12%, -50%) scale(0.86);
  }

  .featured-nav-prev {
    left: 2%;
  }

  .featured-nav-next {
    right: 2%;
  }
}

/* =========================================================
   Mobile
   ========================================================= */

@media (max-width: 767.98px) {
  .camps-page > .container {
    padding-right: 16px;
    padding-left: 16px;
    padding-bottom: 96px;
  }

  /* Banner */

  .camp-banner ::v-deep .carousel-control-prev,
  .camp-banner ::v-deep .carousel-control-next {
    width: 44px;
    height: 44px;
  }

  .camp-banner ::v-deep .carousel-control-prev {
    left: 0.5rem;
  }

  .camp-banner ::v-deep .carousel-control-next {
    right: 0.5rem;
  }

  .camp-banner-empty {
    min-height: 180px;

    font-size: 1.4rem;
  }

  /* Header */

  .camp-discovery-panel {
    margin-bottom: 2rem;
    padding: 1rem;
    border-radius: 14px;
  }

  .camp-discovery-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .camp-discovery-heading h2 {
    font-size: 1.45rem;
  }

  .camp-discovery-panel .series-header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .camp-discovery-panel .series-toolbar {
    gap: 1rem;
  }

  .camp-filter-block,
  .camp-discovery-panel .sort-box {
    width: 100%;
    flex-basis: auto;
  }

  .camp-discovery-panel .camp-type-filter {
    width: 100%;
  }

  .series-header {
    align-items: flex-start;

    flex-direction: column;
  }

  .series-header h2 {
    font-size: 1.45rem;
  }

  .overview-btn {
    min-width: auto;

    white-space: nowrap;
  }

  .series-header-actions {
    width: 100%;

    justify-content: flex-end;
  }

  /* Toolbar */

  .series-toolbar {
    align-items: stretch;

    flex-direction: column;

    min-width: 0;
    max-width: 100%;

    padding: 1rem;
  }

  .camp-type-filter {
    width: 100%;
    min-width: 0;
    max-width: 100%;

    flex-wrap: nowrap;

    gap: 0.5rem;

    margin: 0;

    padding: 0.15rem 0.15rem 0.4rem;

    overflow-x: auto;

    scroll-behavior: smooth;
    scroll-snap-type: x proximity;

    -webkit-overflow-scrolling: touch;

    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .type-btn {
    flex: 0 0 auto;

    min-width: 72px;

    scroll-snap-align: start;
  }

  .sort-box {
    width: 100%;

    .custom-select {
      width: 100%;
    }
  }

  /* Featured */

  .featured-series-section {
    margin-bottom: 2.75rem;
    padding-top: 1.25rem;
  }

  .featured-section-heading {
    margin-bottom: 1rem;

    h2 {
      font-size: 1.45rem;
    }
  }

  .featured-description {
    font-size: 0.85rem;
  }

  .early-bird-section {
    margin-bottom: 2.75rem;
    padding: 1rem 1rem 0.65rem;
    border-radius: 14px;
  }

  .early-bird-heading {
    align-items: flex-start;
    flex-direction: row;
  }

  .early-bird-heading h2 {
    font-size: 1.35rem;
  }

  .early-bird-heading > div:first-child {
    min-width: 0;
  }

  .early-bird-carousel {
    padding-right: 8px;
    padding-left: 8px;
  }

  .early-bird-carousel ::v-deep .splide__arrow--prev {
    left: -0.15rem;
  }

  .early-bird-carousel ::v-deep .splide__arrow--next {
    right: -0.15rem;
  }

  .featured-series-carousel {
    width: 100%;
  }

  .featured-series-stage {
    height: 300px;

    overflow: hidden;
  }

  .featured-series-card {
    display: none;

    width: calc(100% - 8px);
    height: 280px;

    border-radius: 16px;
  }

  .featured-series-card-center {
    display: block;

    height: 280px;

    transform: translate(-50%, -50%);
  }

  .featured-series-card-center:hover {
    transform: translate(-50%, -50%);
  }

  .featured-series-card-center:hover img {
    transform: none;
  }

  .featured-series-content {
    padding: 1rem;

    h3 {
      font-size: 1.1rem;
    }
  }

  .featured-series-price {
    strong {
      font-size: 1rem;
    }
  }

  .featured-series-badge {
    top: 0.75rem;
    left: 0.75rem;

    padding: 0.3rem 0.65rem;

    font-size: 0.72rem;
  }

  /* Featured Navigation */

  .featured-series-carousel.featured-count-1,
  .featured-series-carousel.featured-count-2 {
    .featured-series-stage {
      display: block;
      height: 300px;
      padding: 0;
    }

    .featured-series-card {
      position: absolute;
      top: 50%;
      left: 50%;
      width: calc(100% - 8px);
      max-width: none;
      height: 280px;
      transform: translate(-50%, -50%);
    }

    .featured-series-card-center,
    .featured-series-card-center:hover {
      display: block;
      transform: translate(-50%, -50%);
    }

    .featured-series-card-right {
      display: none;
    }

    .featured-series-card-center:hover img {
      transform: none;
    }

    .featured-nav-prev {
      left: -4px;
    }

    .featured-nav-next {
      right: -4px;
    }
  }

  .featured-nav {
    width: 42px;
    height: 42px;
  }

  .featured-nav-prev {
    left: -4px;
  }

  .featured-nav-next {
    right: -4px;
  }

  /* Pagination */

  .featured-pagination {
    margin-top: 0.65rem;
  }

  /* All Series */

  .all-series-heading {
    h2 {
      font-size: 1.45rem;
    }
  }

  /* Regular card */

  .camp-series-card:hover {
    transform: none;
  }

  .series-meta {
    align-items: flex-start;

    flex-direction: column;

    gap: 0.65rem;
  }

  .series-price {
    margin-top: 0.15rem;
  }

  .series-card-footer {
    align-items: flex-start;
    flex-direction: column;
  }

  .series-card-action {
    width: 100%;
  }
}

/* =========================================================
   Reduced Motion
   ========================================================= */

@media (prefers-reduced-motion: reduce) {
  .featured-series-card,
  .featured-series-card img,
  .featured-series-shade,
  .featured-nav,
  .featured-pagination-dot,
  .camp-series-card,
  .camp-series-cover .card-img {
    transition: none !important;
    animation: none !important;
  }

  .camp-type-filter {
    scroll-behavior: auto;
  }

  .admission-status-label {
    animation: none;
  }
}

.featured-series-description {
  display: -webkit-box;
  overflow: hidden;
  height: 3.6em;
  margin: 0.45rem 0 0.7rem;
  color: rgba(255, 255, 255, 0.86);
  font-size: 0.82rem;
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
</style>
